import { useEffect, useState, useCallback, useRef } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDate, STATUS_COLORS, PAY_COLORS } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import Pagination, { PAGE_SIZE } from "@/components/Pagination";
import LoyaltyCard from "@/components/LoyaltyCard";
import html2canvas from "html2canvas";
import { Award, Gift, Share2, Link as LinkIcon, UserPlus, History } from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { settings, isAdmin } = useAuth();
  const { confirm, prompt } = useConfirm();
  const [c, setC] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedCard, setSelectedCard] = useState("");
  const cardRef = useRef(null);
  const cur = settings?.currency || "KMF";

  const load = useCallback(() => { api.get(`/customers/${id}`).then((r) => setC(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <p className="text-slate-400">Chargement...</p>;

  const rule = c.loyalty_rule;
  const available = c.loyalty_rules_available || [];
  const orders = c.orders || [];
  const pagedOrders = orders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const threshold = rule ? (rule.mode === "per_kg" ? (rule.kg_required || 0) : (rule.visits_required || 0)) : 0;
  const loyalty = c.loyalty_enrolled && rule ? {
    card_number: c.loyalty_card_number,
    customer_name: c.name,
    mode: rule.mode || "per_visit",
    unit: rule.mode === "per_kg" ? "kg" : "passage",
    stamps: c.loyalty_stamps || 0,
    required: threshold,
    remaining: Math.max(0, threshold - (c.loyalty_stamps || 0)),
    rewards_available: c.loyalty_rewards || 0,
    reward_type: rule.reward_type,
    reward_value: rule.reward_value,
    rule_name: rule.name,
  } : null;

  const enroll = async (ruleId) => {
    const rid = ruleId || selectedCard || available[0]?.id;
    if (!rid) { toast.error("Aucune carte disponible. Créez-en une dans le menu Fidélité."); return; }
    try {
      await api.post(`/customers/${id}/loyalty/enroll`, { rule_id: rid });
      toast.success("Carte attribuée — cliquez « WhatsApp » pour l'envoyer en image");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const changeCard = async (ruleId) => {
    if (!ruleId || ruleId === c.loyalty_rule_id) return;
    if (!(await confirm({ title: "Changer de carte", description: "Changer de carte réinitialisera les tampons du client (les récompenses gagnées sont conservées). Continuer ?", confirmText: "Changer" }))) return;
    try {
      await api.post(`/customers/${id}/loyalty/enroll`, { rule_id: ruleId });
      toast.success("Carte changée (tampons réinitialisés)");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const unenroll = async () => { try { await api.post(`/customers/${id}/loyalty/unenroll`); toast.success("Fidélité désactivée"); load(); } catch (e) { toast.error(apiError(e)); } };
  const redeem = async () => { try { await api.post(`/customers/${id}/loyalty/redeem`); toast.success("Récompense utilisée"); load(); } catch (e) { toast.error(apiError(e)); } };
  const adjustLoyalty = async () => {
    const unit = loyalty?.mode === "per_kg" ? "kg" : "passages";
    const val = await prompt({ title: "Ajuster le cumul", inputLabel: `Cumul actuel à enregistrer sur la carte (en ${unit}) :`, inputType: "number", defaultValue: loyalty?.stamps ?? 0, confirmText: "Enregistrer" });
    if (val === null) return;
    const n = Number(val);
    if (isNaN(n) || n < 0) return toast.error("Valeur invalide");
    try { await api.post(`/customers/${id}/loyalty/adjust`, { stamps: n }); toast.success("Cumul ajusté"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const shareCard = async () => {
    const el = cardRef.current;
    if (!el) { toast.error("Carte non prête, réessayez."); return; }
    const greeting = `Bonjour ${c.name}, voici votre carte de fidélité ${settings?.company_name || "WuraPro"} 🎁`;
    try {
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], `carte-fidelite-${(c.name || "client").replace(/\s+/g, "-")}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: greeting });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = file.name; a.click();
        URL.revokeObjectURL(url);
        const phone = (c.phone || "").replace(/\D/g, "");
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(greeting)}`, "_blank");
        toast.success("Image téléchargée — joignez-la à votre message WhatsApp");
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
      toast.error("Partage impossible");
    }
  };
  const redeemBonus = async () => { try { await api.post(`/customers/${id}/bonus/redeem`); toast.success("Récompense de parrainage utilisée"); load(); } catch (e) { toast.error(apiError(e)); } };
  const referralMsg = () => `Bonjour, parrainez vos proches chez ${settings?.company_name || "WuraPro"} ! Donnez ce code à votre arrivée : ${c.referral_code}. Vous gagnez tous les deux une récompense.`;
  const shareReferral = () => {
    const phone = (c.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Ce client n'a pas de numéro de téléphone."); return; }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(referralMsg())}`, "_blank");
  };
  const copyReferral = async () => { try { await navigator.clipboard.writeText(c.referral_code || ""); toast.success("Code de parrainage copié"); } catch { toast.error("Copie impossible"); } };
  const notifyReferrer = () => {
    const phone = (c.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Ce client n'a pas de numéro de téléphone."); return; }
    const tpl = c.referrer_notify_message || "Bonjour {name} 🎉 Felicitations ! Grace a vos parrainages, vous avez debloque une recompense : {reward}. Passez la recuperer a la laverie {company}. Merci de votre confiance 🙏";
    const msg = tpl.replace(/{name}/g, c.name).replace(/{reward}/g, c.referrer_reward_label || "votre recompense").replace(/{company}/g, settings?.company_name || "WuraPro");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-3 gap-5">
        <Card className="p-5 md:col-span-1">
          <h2 className="text-xl font-bold text-slate-800" data-testid="customer-detail-name">{c.name}</h2>
          <p className="text-slate-500">{c.phone}</p>
          {c.address && <p className="text-slate-500 text-sm mt-1">{c.address}</p>}
          {c.notes && <p className="text-slate-400 text-sm mt-2 italic">{c.notes}</p>}

          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 text-slate-700 font-medium mb-3"><Award size={18} className="text-amber-500" /> Fidélité</div>
            {available.length === 0 && !c.loyalty_enrolled ? (
              <p className="text-sm text-slate-400">Aucune carte disponible. Créez-en une dans le menu Fidélité.</p>
            ) : !c.loyalty_enrolled ? (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Choisir une carte</Label>
                <select
                  data-testid="enroll-card-select"
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                  value={selectedCard || available[0]?.id || ""}
                  onChange={(e) => setSelectedCard(e.target.value)}
                >
                  {available.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} — {r.mode === "per_kg" ? `${r.kg_required} kg` : `${r.visits_required} passages`}
                    </option>
                  ))}
                </select>
                <Button data-testid="enroll-loyalty-btn" onClick={() => enroll()} className="bg-amber-500 hover:bg-amber-600 text-white w-full"><Award size={16} className="mr-2" /> Attribuer la carte</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div ref={cardRef}><LoyaltyCard loyalty={loyalty} settings={settings || {}} /></div>
                <div>
                  <Label className="text-xs text-slate-500">Carte du client</Label>
                  <select
                    data-testid="change-card-select"
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white mt-1"
                    value={c.loyalty_rule_id || ""}
                    onChange={(e) => changeCard(e.target.value)}
                  >
                    {!available.some((r) => r.id === c.loyalty_rule_id) && rule && (
                      <option value={c.loyalty_rule_id}>{rule.name} (inactive)</option>
                    )}
                    {available.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" data-testid="redeem-loyalty-btn" onClick={redeem} disabled={loyalty.rewards_available <= 0} title={loyalty.rewards_available <= 0 ? "Aucune récompense disponible pour le moment" : "Utiliser une récompense"} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-100"><Gift size={14} className="mr-1" /> Utiliser récompense</Button>
                  <Button size="sm" variant="outline" data-testid="share-loyalty-btn" onClick={shareCard}><Share2 size={14} className="mr-1" /> WhatsApp</Button>
                  {isAdmin && <Button size="sm" variant="outline" data-testid="adjust-loyalty-btn" onClick={adjustLoyalty}><Award size={14} className="mr-1" /> Ajuster {loyalty.mode === "per_kg" ? "kg" : "tampons"}</Button>}
                  <Button size="sm" variant="outline" className="text-red-600" data-testid="unenroll-loyalty-btn" onClick={unenroll}>Désactiver</Button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 text-slate-700 font-medium mb-2"><UserPlus size={18} className="text-sky-500" /> Parrainage</div>
            <p className="text-xs text-slate-500">Code de parrainage du client</p>
            <div className="flex items-center gap-2 mt-1">
              <span data-testid="referral-code" className="font-mono font-semibold text-slate-800 bg-slate-100 rounded px-2 py-1">{c.referral_code || "—"}</span>
              <Button size="sm" variant="outline" data-testid="share-referral-btn" onClick={shareReferral}><Share2 size={14} className="mr-1" /> Partager</Button>
              <Button size="sm" variant="outline" data-testid="copy-referral-btn" onClick={copyReferral}><LinkIcon size={14} className="mr-1" /> Copier</Button>
            </div>
            {c.referred_by_name && <p className="text-sm text-slate-500 mt-2">Parrainé par : <b>{c.referred_by_name}</b></p>}
            {c.ambassador_code && <p className="text-sm text-slate-500 mt-2">⭐ Ambassadeur — code <span className="font-mono font-semibold text-slate-700">{c.ambassador_code}</span> (-{c.ambassador_percent}%)</p>}
            {c.referrer_reward_threshold > 0 && (
              <p className="text-sm text-slate-500 mt-2" data-testid="referrer-progress">Progression récompense : <b>{c.referrer_points || 0}/{c.referrer_reward_threshold}</b> filleuls</p>
            )}
            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm">Récompenses : <span data-testid="bonus-rewards" className="font-semibold text-emerald-600">{c.bonus_rewards || 0}</span> {c.referrer_reward_label ? `· ${c.referrer_reward_label}` : ""}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" data-testid="notify-referrer-btn" onClick={notifyReferrer} disabled={(c.bonus_rewards || 0) <= 0} title={(c.bonus_rewards || 0) <= 0 ? "Disponible une fois une récompense débloquée" : "Notifier par WhatsApp"} className="disabled:bg-slate-100 disabled:text-slate-400 disabled:opacity-100"><Share2 size={14} className="mr-1" /> Notifier</Button>
                <Button size="sm" data-testid="redeem-bonus-btn" onClick={redeemBonus} disabled={(c.bonus_rewards || 0) <= 0} title={(c.bonus_rewards || 0) <= 0 ? "Aucune récompense disponible pour le moment" : "Utiliser une récompense"} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-100"><Gift size={14} className="mr-1" /> Utiliser</Button>
              </div>
            </div>
          </div>

          {(c.loyalty_history || []).length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-slate-700 font-medium mb-2"><History size={18} className="text-slate-400" /> Historique fidélité</div>
              <ul data-testid="loyalty-history" className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                {(c.loyalty_history || []).slice(0, 20).map((ev, i) => (
                  <li key={i} className="flex items-start justify-between gap-2 text-sm">
                    <span className="text-slate-700">{ev.message}</span>
                    <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(ev.date)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        <Card className="p-5 md:col-span-2">
          <h3 className="font-semibold text-slate-800 mb-3">Historique des commandes ({orders.length})</h3>
          <div className="space-y-2">
            {pagedOrders.map((o) => (
              <div key={o.id} data-testid={`history-order-${o.id}`} onClick={() => nav(`/orders/${o.id}`)} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer">
                <div>
                  <p className="font-medium text-slate-800">{o.order_number}</p>
                  <p className="text-xs text-slate-500">{fmtDate(o.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{fmtMoney(o.total, cur)}</p>
                  <div className="flex gap-1 justify-end mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${PAY_COLORS[o.payment_status]}`}>{o.payment_status}</span>
                  </div>
                </div>
              </div>
            ))}
            {orders.length === 0 && <p className="text-slate-400 text-sm">Aucune commande</p>}
          </div>
          <Pagination page={page} total={orders.length} onPage={setPage} />
        </Card>
      </div>
    </div>
  );
}
